import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CHAT_URL = `${BACKEND_URL}/api/chat`;
const SUGGESTIONS = [
  "How many buyers do we have?",
  "Which buyer ordered the most this year?",
  "Show pending orders assigned to Rajendra",
  "How many Dew Drop cartons in total?",
];

// A small floating chat bubble that opens a Claude-powered business assistant.
// Streams tokens via SSE from POST /api/chat (auth via httpOnly cookie).
export default function ChatBubble() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role: user|assistant, content}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(CHAT_URL, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message }),
      });
      if (!res.ok || !res.body) {
        const errBody = await res.text();
        throw new Error(errBody || `HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Parse SSE frames — each ends in \n\n
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const frame = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 2);
          if (!frame.startsWith("data:")) continue;
          try {
            const evt = JSON.parse(frame.slice(5).trim());
            if (evt.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + evt.delta };
                return copy;
              });
            } else if (evt.done) {
              if (evt.session_id) setSessionId(evt.session_id);
            } else if (evt.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: `⚠️ ${evt.error}` };
                return copy;
              });
            }
          } catch { /* ignore malformed frame */ }
        }
      }
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: `⚠️ ${err.message || "Chat failed"}` };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating bubble trigger */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-bubble-toggle"
          className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-forest text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 grid place-items-center"
          aria-label="Open assistant"
        >
          <MessageSquare className="h-6 w-6" strokeWidth={1.8} />
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-6 right-6 z-40 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-3rem)] card-surface flex flex-col overflow-hidden shadow-2xl"
          data-testid="chat-panel"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-line flex items-center justify-between bg-forest text-white">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-white/15 grid place-items-center">
                <MessageSquare className="h-4 w-4" />
              </div>
              <div>
                <div className="font-outfit font-semibold text-sm">Business Assistant</div>
                <div className="text-[11px] opacity-80 font-figtree">Claude Sonnet · knows your data</div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chat-close-btn"
              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="chat-messages"
            className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-bone/40"
          >
            {messages.length === 0 && (
              <div className="text-center py-4">
                <div className="text-sm font-figtree text-ink-soft mb-3">
                  Ask anything about your orders, buyers, employees, or products.
                </div>
                <div className="flex flex-col gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="text-left text-xs font-figtree text-forest bg-white border border-line rounded-lg px-3 py-2 hover:bg-forest-soft transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}-${i}`}
                className={`max-w-[85%] px-3 py-2 rounded-lg text-sm font-figtree whitespace-pre-wrap leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-forest text-white"
                    : "mr-auto bg-white text-ink border border-line"
                }`}
              >
                {m.content || (busy && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : "")}
              </div>
            ))}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-line p-3 flex items-center gap-2 bg-white"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about your business…"
              data-testid="chat-input"
              disabled={busy}
              className="flex-1 h-10 px-3 rounded-lg border border-line outline-none focus:ring-2 focus:ring-forest text-sm font-figtree"
            />
            <button
              type="submit"
              data-testid="chat-send-btn"
              disabled={busy || !input.trim()}
              className="h-10 w-10 rounded-lg bg-forest text-white grid place-items-center disabled:opacity-40 disabled:cursor-not-allowed hover:bg-forest-hover transition-colors"
              aria-label="Send"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
